import { ReviewPanel, AgentEmptyState } from '../ReviewPanel';
import { CheckpointActions } from '../CheckpointActions';
import type { AgentOutputRow, ArtifactRow } from '@/hooks/useJobProgress';

interface LumenPayload {
  page_label: string;
  storage_id?: string;
  url?: string | null;
  bytes?: number;
  width?: number | null;
  height?: number | null;
  revision_index?: number;
}

interface LumenPanelProps {
  jobId: string;
  outputs: AgentOutputRow[];
  artifacts: ArtifactRow[];
  isRunning?: boolean;
}

export function LumenPanel({ jobId, outputs, artifacts, isRunning }: LumenPanelProps) {
  if (outputs.length === 0) {
    return (
      <ReviewPanel agent="lumen" index={5}>
        <AgentEmptyState agent="lumen" isRunning={isRunning} />
      </ReviewPanel>
    );
  }

  // Match each LUMEN output with its artifact (which carries the signed URL).
  const artifactByLabel = new Map<string, ArtifactRow>();
  for (const a of artifacts) {
    if (a.kind === 'hifi_page_image' && a.ref) artifactByLabel.set(a.ref, a);
  }

  return (
    <ReviewPanel
      agent="lumen"
      index={5}
      subtitle="Hi-fi mockups"
      rightTag={`${outputs.length} page${outputs.length > 1 ? 's' : ''}`}
    >
      <div className="space-y-8">
        {outputs.map((o) => {
          const payload = o.payload as LumenPayload;
          const art = artifactByLabel.get(payload.page_label);
          return (
            <div key={o._id} className="space-y-3">
              <div className="flex items-baseline justify-between">
                <h3 className="font-serif text-xl italic text-ink">{payload.page_label}</h3>
                <p className="font-mono text-[11px] text-graphite">
                  rev {payload.revision_index ?? 0}
                  {payload.width && payload.height ? ` · ${payload.width}×${payload.height}` : ''}
                </p>
              </div>
              <div className="overflow-hidden rounded-sm border border-ink/10 bg-paper-2">
                {art?.url ? (
                  <img src={art.url} alt={`Hi-fi mockup of ${payload.page_label}`} className="block w-full" />
                ) : (
                  <div className="aspect-video grid place-items-center text-graphite text-[12px]">
                    Image not yet available
                  </div>
                )}
              </div>
              <CheckpointActions
                jobId={jobId}
                outputId={o._id}
                agent="lumen"
                itemKey={payload.page_label}
                disabled={o.reviewStatus !== 'awaiting_user'}
              />
            </div>
          );
        })}
      </div>
    </ReviewPanel>
  );
}
